import { useCallback, useEffect, useRef, useState } from 'react';
import { updateVideoReadyState } from '../videoReady';

interface UseCameraStreamOptions {
  autoStart?: boolean;
  videoConstraints?: MediaTrackConstraints;
}

export function getAdaptiveVideoConstraints(): MediaTrackConstraints {
  const isPortrait = typeof window !== 'undefined' && window.innerHeight > window.innerWidth;
  return {
    facingMode: { ideal: 'environment' },
    width: { ideal: isPortrait ? 1080 : 1920 },
    height: { ideal: isPortrait ? 1920 : 1080 },
    aspectRatio: { ideal: isPortrait ? 9 / 16 : 16 / 9 }
  };
}

const useCameraStream = ({
  autoStart = true,
  videoConstraints
}: UseCameraStreamOptions = {}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const videoReadyHandlerRef = useRef<(() => void) | null>(null);
  const videoDataHandlerRef = useRef<(() => void) | null>(null);
  const startedRef = useRef(false);
  const isMountedRef = useRef(true);
  const [streamActive, setStreamActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const stopCamera = useCallback(() => {
    const stream = streamRef.current;
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      if (videoReadyHandlerRef.current) {
        videoRef.current.removeEventListener('loadedmetadata', videoReadyHandlerRef.current);
        videoReadyHandlerRef.current = null;
      }
      if (videoDataHandlerRef.current) {
        videoRef.current.removeEventListener('loadeddata', videoDataHandlerRef.current);
        videoDataHandlerRef.current = null;
      }
      videoRef.current.srcObject = null;
    }
    setStreamActive(false);
    startedRef.current = false;
  }, []);

  const startCamera = useCallback(async (constraints?: MediaTrackConstraints) => {
    if (startedRef.current) {
      return;
    }
    startedRef.current = true;
    setError(null);
    try {
      const activeConstraints = constraints ?? videoConstraints ?? getAdaptiveVideoConstraints();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: activeConstraints,
        audio: false
      });
      if (!isMountedRef.current) {
        stream.getTracks().forEach(track => track.stop());
        return;
      }
      streamRef.current = stream;
      if (videoRef.current) {
        const videoEl = videoRef.current;
        videoEl.setAttribute('playsinline', 'true');
        videoEl.setAttribute('webkit-playsinline', 'true');
        videoEl.muted = true;
        videoEl.srcObject = stream;
        setStreamActive(false);
        try {
          await videoEl.play();
        } catch {
          // ignore autoplay failures
        }
        updateVideoReadyState({
          videoEl,
          onReady: () => setStreamActive(true),
          metadataHandlerRef: videoReadyHandlerRef,
          dataHandlerRef: videoDataHandlerRef
        });
      }
    } catch {
      startedRef.current = false;
      setError('Camera access denied. Please enable camera permissions.');
    }
  }, [videoConstraints]);

  useEffect(() => {
    if (!autoStart) {
      return;
    }
    startCamera();
    return () => stopCamera();
  }, [autoStart, startCamera, stopCamera]);

  const clearError = useCallback(() => setError(null), []);

  return {
    videoRef,
    streamRef,
    streamActive,
    error,
    startCamera,
    stopCamera,
    clearError
  };
};

export default useCameraStream;
